import { useState, useRef, useEffect } from 'react';
import { sendQuoteEmail } from '../apiCalls';
import { trackJourney } from '../utils/journey';

export const isDownloadResponse = (result) => Boolean(result?.method === 'download'
    && typeof result.pdf === 'string' && result.pdf.trim());

const triggerPdfDownload = (base64Pdf) => {
    const bytes = atob(base64Pdf);
    if (!bytes.startsWith('%PDF-')) throw new Error('Invalid PDF response');
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quote_${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const useEmailQuote = () => {
    const [sendResponse, setSendResponse] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        return () => clearTimeout(timerRef.current);
    }, []);

    const sendQuote = async (data) => {
        clearTimeout(timerRef.current);
        setIsSending(true);
        setSendResponse(null);

        try {
            const response = await sendQuoteEmail(data);
            const result = response.data;

            if (!result || typeof result !== 'object' || Array.isArray(result)
                || result.code || result.error || result.success === false || result.isSuccess === false
                || (result.message != null && (typeof result.message !== 'string' || !result.message.trim()))) {
                throw new Error('Invalid quote confirmation');
            }
            if (isDownloadResponse(result)) {
                triggerPdfDownload(result.pdf);
                setSendResponse({
                    text: result.message || 'Your quote has been downloaded.',
                    variant: 'warning',
                });
            } else {
                if (typeof result.message !== 'string' || !result.message.trim()
                    || (result.method && result.method !== 'email')
                    || /\b(not|never|failed|failure|error|unable|cannot|can't|couldn't|could not|wasn't|weren't|isn't|aren't|hasn't|haven't|hadn't|didn't|don't|doesn't|won't|wouldn't|pending|awaiting|queued|processing|sending|will|soon|unsent|undelivered|unsuccessful|unsuccessfully|undeliverable|rejected|cancelled|canceled)\b/i.test(result.message)
                    || !/\b(sent|delivered|emailed)\b/i.test(result.message)) {
                    throw new Error('Invalid quote confirmation');
                }
                setSendResponse({ text: result.message, variant: 'success' });
            }
            trackJourney('lead_submitted');
            trackJourney('quote_delivered');
            timerRef.current = setTimeout(() => setSendResponse(null), 8000);
        } catch (err) {
            setSendResponse({
                text: typeof err?.friendlyMessage === 'string' && err.friendlyMessage.trim()
                    ? err.friendlyMessage : 'There was a problem sending your quote. Please try again.',
                variant: 'danger',
            });
        } finally {
            setIsSending(false);
        }
    };

    return { sendResponse, isSending, sendQuote };
};
